import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import {
  ArrowLeft, User, Bell, Monitor, Shield, ChevronRight,
  Mail, Smartphone, Moon, Sun, Globe, Trash2, Loader2, KeyRound
} from "lucide-react";
import { useAuth } from "../../contexts/AuthContext";
import { supabase } from "../../lib/supabaseClient";

const NAVY = "#2a2420";
const ORANGE = "#607840";
const SURFACE = "#fff";
const RAISED = "#fff";
const CANVAS = "#fff";
const LINE = "#e9e1d4";
const MUTED = "#8a8074";
const CHIP = "#f3ece1";
const SHADOW = "0 1px 3px rgba(42,36,32,.08), 0 12px 28px rgba(42,36,32,.06)";
const ACCENT_WEAK = "rgba(96,120,64,0.12)";
const ACCENT_LINE = "rgba(96,120,64,0.30)";
const ACCENT_SHADOW = "0 2px 8px rgba(96,120,64,0.25)";
const UP = "#c2412e";
const UP_WEAK = "rgba(194,65,46,0.12)";
const UP_LINE = "rgba(194,65,46,0.24)";
const DOWN = "#3c7d57";
const DOWN_WEAK = "rgba(60,125,87,0.12)";
const DOWN_LINE = "rgba(60,125,87,0.24)";

interface Prefs {
  emailNotifications: boolean;
  inAppNotifications: boolean;
  productUpdates: boolean;
  memberActivity: boolean;
  menuCostAutoUpdatePrices: boolean;
}

interface ProfileForm {
  name: string;
  phone: string;
}

const DEFAULTS: Prefs = {
  emailNotifications: true,
  inAppNotifications: true,
  productUpdates: true,
  memberActivity: false,
  menuCostAutoUpdatePrices: true,
};

function loadPrefs(): Prefs {
  try {
    const raw = localStorage.getItem("iscan_prefs");
    if (raw) return { ...DEFAULTS, ...JSON.parse(raw) };
  } catch {}
  return DEFAULTS;
}

function Toggle({
  checked,
  onChange,
}: {
  checked: boolean;
  onChange: (v: boolean) => void;
}) {
  return (
    <button
      type="button"
      role="switch"
      aria-checked={checked}
      onClick={() => onChange(!checked)}
      className="relative inline-flex h-5 w-9 flex-shrink-0 rounded-full transition-colors duration-200 ease-in-out focus:outline-none"
      style={{
        backgroundColor: checked ? ORANGE : CHIP,
        boxShadow: `inset 0 0 0 1px ${checked ? ACCENT_LINE : LINE}`,
      }}
    >
      <span
        className="pointer-events-none inline-block h-4 w-4 rounded-full transform transition-transform duration-200 ease-in-out"
        style={{
          marginTop: "2px",
          marginLeft: "2px",
          backgroundColor: SURFACE,
          boxShadow: "0 1px 4px rgba(80,55,30,0.18)",
          transform: checked ? "translateX(16px)" : "translateX(0)",
        }}
      />
    </button>
  );
}

function Section({
  title,
  children,
}: {
  title: string;
  children: React.ReactNode;
}) {
  return (
    <div className="mb-6">
      <h2 className="text-[11px] font-semibold uppercase mb-3" style={{ color: MUTED }}>
        {title}
      </h2>
      <div
        className="rounded-xl overflow-hidden"
        style={{
          backgroundColor: SURFACE,
          border: `1px solid ${LINE}`,
          boxShadow: SHADOW,
        }}
      >
        {children}
      </div>
    </div>
  );
}

function Row({
  icon: Icon,
  label,
  sublabel,
  right,
  last,
}: {
  icon: React.ElementType;
  label: string;
  sublabel?: string;
  right?: React.ReactNode;
  last?: boolean;
}) {
  return (
    <div
      className="flex items-center gap-4 px-5 py-4"
      style={{ borderBottom: last ? undefined : `1px solid ${LINE}` }}
    >
      <div
        className="w-8 h-8 flex items-center justify-center flex-shrink-0"
      >
        <Icon size={15} style={{ color: MUTED }} />
      </div>
      <div className="flex-1 min-w-0">
        <p className="text-sm font-medium" style={{ color: NAVY }}>{label}</p>
        {sublabel && (
          <p className="text-xs mt-0.5 truncate" style={{ color: MUTED }}>
            {sublabel}
          </p>
        )}
      </div>
      {right}
    </div>
  );
}

export default function SettingsPage({ embedded = false }: { embedded?: boolean }) {
  const navigate = useNavigate();
  const { user, signOut } = useAuth();
  const [prefs, setPrefs] = useState<Prefs>(loadPrefs);
  const [profile, setProfile] = useState<ProfileForm>({
    name: "",
    phone: "",
  });
  const [profileLoading, setProfileLoading] = useState(true);
  const [profileSaving, setProfileSaving] = useState(false);
  const [profileMessage, setProfileMessage] = useState<string | null>(null);
  const [profileError, setProfileError] = useState<string | null>(null);
  const [newPassword, setNewPassword] = useState("");
  const [confirmPassword, setConfirmPassword] = useState("");
  const [passwordSaving, setPasswordSaving] = useState(false);
  const [passwordMessage, setPasswordMessage] = useState<string | null>(null);
  const [passwordError, setPasswordError] = useState<string | null>(null);

  useEffect(() => {
    localStorage.setItem("iscan_prefs", JSON.stringify(prefs));
  }, [prefs]);

  useEffect(() => {
    if (!user) return;
    const metadata = user.user_metadata || {};
    setProfile({
      name: metadata.name || metadata.full_name || metadata.display_name || "",
      phone: metadata.phone || user.phone || "",
    });

    supabase
      .from("profiles")
      .select("name, phone")
      .eq("id", user.id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error && error.code !== "PGRST116") {
          setProfileError(error.message);
        }
        if (data) {
          setProfile({
            name: data.name || metadata.name || "",
            phone: data.phone || metadata.phone || user.phone || "",
          });
        }
        setProfileLoading(false);
      });
  }, [user]);

  function set(key: keyof Prefs, val: boolean) {
    setPrefs((p) => ({ ...p, [key]: val }));
  }

  function updateProfile(key: keyof ProfileForm, value: string) {
    setProfile((current) => ({ ...current, [key]: value }));
  }

  async function saveProfile() {
    if (!user) return;
    setProfileSaving(true);
    setProfileError(null);
    setProfileMessage(null);

    const clean = {
      name: profile.name.trim(),
      phone: profile.phone.trim(),
    };

    // Best-effort auth metadata sync — profiles table is the source of truth
    await supabase.auth.updateUser({ data: clean }).catch(() => {});

    const { error: profileUpdateError } = await supabase
      .from("profiles")
      .upsert({
        id: user.id,
        email: user.email,
        ...clean,
        updated_at: new Date().toISOString(),
      });

    setProfileSaving(false);

    if (profileUpdateError) {
      setProfileError(profileUpdateError.message);
      return;
    }

    setProfileMessage("Profile updated.");
    window.setTimeout(() => setProfileMessage(null), 2500);
  }

  async function changePassword() {
    setPasswordError(null);
    setPasswordMessage(null);

    if (newPassword.length < 6) {
      setPasswordError("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      setPasswordError("Passwords do not match.");
      return;
    }

    setPasswordSaving(true);
    const { error } = await supabase.auth.updateUser({ password: newPassword });
    setPasswordSaving(false);

    if (error) {
      setPasswordError(error.message);
      return;
    }

    setNewPassword("");
    setConfirmPassword("");
    setPasswordMessage("Password updated.");
    window.setTimeout(() => setPasswordMessage(null), 2500);
  }

  const joinedDate = user?.created_at
    ? new Date(user.created_at).toLocaleDateString(undefined, { year: "numeric", month: "long", day: "numeric" })
    : null;

  return (
    <div
      className={embedded ? "p-6" : "h-full min-h-0 overflow-y-auto p-6"}
      style={{
        backgroundColor: embedded ? "transparent" : CANVAS,
        fontFamily: "var(--font-ui)",
      }}
    >
      <div className="w-full max-w-6xl mx-auto">
        {!embedded && (
          <div className="mb-8">
            <button
              onClick={() => navigate("/console-settings")}
              className="flex items-center gap-2 text-sm mb-5 transition-opacity hover:opacity-70"
              style={{ color: NAVY, opacity: 0.55 }}
            >
              <ArrowLeft size={16} />
              Back to settings
            </button>
            <div className="flex items-start gap-4">
              <div
                className="w-12 h-12 rounded-xl flex items-center justify-center flex-shrink-0"
                style={{ backgroundColor: ACCENT_WEAK, color: ORANGE, border: `1px solid ${ACCENT_LINE}` }}
              >
                <User size={22} />
              </div>
              <div>
                <h1 className="text-[27px] font-semibold leading-tight" style={{ color: NAVY }}>Account Preferences</h1>
                <p className="text-sm mt-1" style={{ color: MUTED }}>
                  Manage your account details, profile information, and notification preferences.
                </p>
              </div>
            </div>
          </div>
        )}

        <div className="max-w-4xl">

        {/* Account */}
        <Section title="Account">
          <div className="px-5 py-4" style={{ borderBottom: `1px solid ${LINE}` }}>
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-8 h-8 flex items-center justify-center flex-shrink-0"
              >
                <User size={15} style={{ color: MUTED }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: NAVY }}>{user?.email ?? "—"}</p>
                {joinedDate && (
                  <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                    Member since {joinedDate}
                  </p>
                )}
              </div>
            </div>

            {profileLoading ? (
              <Loader2 size={18} className="animate-spin" style={{ color: ORANGE }} />
            ) : (
              <div className="space-y-3">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                  <ProfileField label="Full name">
                    <input value={profile.name} onChange={(e) => updateProfile("name", e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={profileInputStyle()} />
                  </ProfileField>
                  <ProfileField label="Phone">
                    <input value={profile.phone} onChange={(e) => updateProfile("phone", e.target.value)} className="w-full rounded-lg px-3 py-2 text-sm outline-none" style={profileInputStyle()} />
                  </ProfileField>
                </div>
                {(profileError || profileMessage) && (
                  <p
                    className="text-xs rounded-lg px-3 py-2"
                    style={{
                      color: profileError ? UP : DOWN,
                      backgroundColor: profileError ? UP_WEAK : DOWN_WEAK,
                      border: `1px solid ${profileError ? UP_LINE : DOWN_LINE}`,
                    }}
                  >
                    {profileError || profileMessage}
                  </p>
                )}
                <button
                  onClick={saveProfile}
                  disabled={profileSaving}
                  className="rounded-lg px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                  style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
                >
                  {profileSaving ? "Saving…" : "Save profile"}
                </button>
              </div>
            )}
          </div>
          <Row
            icon={Globe}
            label="Account type"
            sublabel="Standard"
            last
          />
        </Section>

        {/* Security */}
        <Section title="Security">
          <div className="px-5 py-4">
            <div className="flex items-center gap-4 mb-4">
              <div
                className="w-8 h-8 flex items-center justify-center flex-shrink-0"
              >
                <KeyRound size={15} style={{ color: MUTED }} />
              </div>
              <div>
                <p className="text-sm font-medium" style={{ color: NAVY }}>Change password</p>
                <p className="text-xs mt-0.5" style={{ color: MUTED }}>
                  Choose a new password for your Bill Lens account.
                </p>
              </div>
            </div>

            <div className="space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <ProfileField label="New password">
                  <input
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    value={newPassword}
                    onChange={(e) => {
                      setNewPassword(e.target.value);
                      setPasswordError(null);
                      setPasswordMessage(null);
                    }}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={profileInputStyle()}
                  />
                </ProfileField>
                <ProfileField label="Confirm password">
                  <input
                    type="password"
                    autoComplete="new-password"
                    minLength={6}
                    value={confirmPassword}
                    onChange={(e) => {
                      setConfirmPassword(e.target.value);
                      setPasswordError(null);
                      setPasswordMessage(null);
                    }}
                    className="w-full rounded-lg px-3 py-2 text-sm outline-none"
                    style={profileInputStyle()}
                  />
                </ProfileField>
              </div>

              {(passwordError || passwordMessage) && (
                <p
                  className="text-xs rounded-lg px-3 py-2"
                  style={{
                    color: passwordError ? UP : DOWN,
                    backgroundColor: passwordError ? UP_WEAK : DOWN_WEAK,
                    border: `1px solid ${passwordError ? UP_LINE : DOWN_LINE}`,
                  }}
                >
                  {passwordError || passwordMessage}
                </p>
              )}

              <button
                onClick={changePassword}
                disabled={passwordSaving || !newPassword || !confirmPassword}
                className="rounded-lg px-4 py-2 text-xs font-bold text-white disabled:opacity-50"
                style={{ backgroundColor: ORANGE, boxShadow: ACCENT_SHADOW }}
              >
                {passwordSaving ? "Updating..." : "Update password"}
              </button>
            </div>
          </div>
        </Section>

        {/* Menu Costing */}
        <Section title="Menu Costing">
          <Row
            icon={Monitor}
            label="Auto-update active supplier prices"
            sublabel="Use the supplier product bought most recently from invoice scans. Turn off to keep manual supplier choices."
            last
            right={
              <Toggle
                checked={prefs.menuCostAutoUpdatePrices}
                onChange={(v) => set("menuCostAutoUpdatePrices", v)}
              />
            }
          />
        </Section>

        {/* Notifications */}
        <Section title="Notifications">
          <Row
            icon={Bell}
            label="In-app notifications"
            sublabel="Alerts and updates within Bill Lens"
            right={
              <Toggle
                checked={prefs.inAppNotifications}
                onChange={(v) => set("inAppNotifications", v)}
              />
            }
          />
          <Row
            icon={Mail}
            label="Email notifications"
            sublabel="Receive updates at your email"
            right={
              <Toggle
                checked={prefs.emailNotifications}
                onChange={(v) => set("emailNotifications", v)}
              />
            }
          />
          <Row
            icon={Smartphone}
            label="Product updates"
            sublabel="New features and release notes"
            right={
              <Toggle
                checked={prefs.productUpdates}
                onChange={(v) => set("productUpdates", v)}
              />
            }
          />
          <Row
            icon={Bell}
            label="Member activity"
            sublabel="When teammates join or leave a business"
            last
            right={
              <Toggle
                checked={prefs.memberActivity}
                onChange={(v) => set("memberActivity", v)}
              />
            }
          />
        </Section>

        {/* Appearance */}
        <Section title="Appearance">
          <Row
            icon={Sun}
            label="Theme"
            sublabel="Light (default)"
            last
            right={
              <span
                className="text-xs px-2.5 py-1 rounded-full font-medium"
                style={{ backgroundColor: CHIP, color: MUTED, border: `1px solid ${LINE}` }}
              >
                Coming soon
              </span>
            }
          />
        </Section>

        {/* Privacy */}
        <Section title="Privacy & Data">
          <Row
            icon={Shield}
            label="Data & privacy"
            sublabel="Your data is stored securely via Supabase"
          />
          <Row
            icon={Trash2}
            label="Delete account"
            sublabel="Permanently remove your account and data"
            last
            right={
              <button
                className="text-xs px-3 py-1.5 rounded-lg font-medium transition-colors"
                style={{
                  color: UP,
                  backgroundColor: UP_WEAK,
                  border: `1px solid ${UP_LINE}`,
                }}
                onClick={() => alert("Please contact support to delete your account.")}
              >
                Delete
              </button>
            }
          />
        </Section>

        {/* Sign out */}
        <button
          onClick={async () => {
            await signOut();
            navigate("/login", { replace: true });
          }}
          className="w-full flex items-center justify-center gap-2 py-3 rounded-xl text-sm font-semibold transition-colors"
          style={{
            color: UP,
            backgroundColor: UP_WEAK,
            border: `1.5px solid ${UP_LINE}`,
          }}
        >
          Sign out of Bill Lens
        </button>

        <p className="text-center text-xs mt-6" style={{ color: MUTED }}>
          Bill Lens · v1.0.0
        </p>
        </div>
      </div>
    </div>
  );
}

function ProfileField({ label, children }: { label: string; children: React.ReactNode }) {
  return (
    <label className="block">
      <span className="block text-[10px] font-semibold uppercase mb-1" style={{ color: MUTED }}>
        {label}
      </span>
      {children}
    </label>
  );
}

function profileInputStyle(): React.CSSProperties {
  return {
    border: `1.5px solid ${LINE}`,
    color: NAVY,
    backgroundColor: RAISED,
  };
}
